import {
  loadCrosshairConfig,
  notifyCrosshairChange,
  type CrosshairConfig,
  type CrosshairStyle,
} from "../store/crosshairStore";

/**
 * CrosshairSettings — toggle the editor panel with `C`. Sliders write
 * straight into the CrosshairStore; the live Crosshair subscribes and
 * re-renders instantly so the player dials in their preference in real
 * time.
 *
 * Sprint 6 spec covers: color (RGBA), thickness, gap, outline (on/off
 * + color + thickness), style (cross / T / dot / cross+dot), dynamic
 * on/off. CS:GO import/export string is a stretch — JSON for now.
 */
export class CrosshairSettings {
  private readonly root: HTMLDivElement;
  private cfg: CrosshairConfig;
  private isOpen = false;
  private readonly onKeyDown: (e: KeyboardEvent) => void;

  constructor() {
    this.cfg = loadCrosshairConfig();
    this.root = document.createElement("div");
    this.root.id = "crosshair-settings";
    this.root.classList.add("hidden");
    this.root.innerHTML = this.renderHtml();
    document.body.appendChild(this.root);
    this.injectStyles();
    this.bindControls();

    this.onKeyDown = (e: KeyboardEvent) => {
      if (e.repeat || e.ctrlKey || e.metaKey || e.altKey) return;
      if (document.body.classList.contains("buy-open")) return;
      if (document.body.classList.contains("chat-open")) return;
      if (e.code === "KeyC" && !e.shiftKey) {
        this.toggle();
        e.preventDefault();
      } else if (this.isOpen && e.code === "Escape") {
        this.close();
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", this.onKeyDown, { capture: true });
  }

  destroy(): void {
    window.removeEventListener("keydown", this.onKeyDown, { capture: true } as any);
    this.root.remove();
  }

  private toggle(): void {
    this.isOpen ? this.close() : this.open();
  }

  private open(): void {
    this.isOpen = true;
    this.root.classList.remove("hidden");
    document.body.classList.add("xh-settings-open");
  }

  private close(): void {
    this.isOpen = false;
    this.root.classList.add("hidden");
    document.body.classList.remove("xh-settings-open");
  }

  private renderHtml(): string {
    const c = this.cfg;
    const styleOptions: CrosshairStyle[] = ["cross", "t-shape", "dot", "cross-dot"];
    const styleButtons = styleOptions
      .map(
        (s) =>
          `<button data-style="${s}" class="xs-style ${s === c.style ? "active" : ""}">${labelForStyle(s)}</button>`,
      )
      .join("");
    return `
      <div class="xs-panel">
        <div class="xs-header">
          <span class="xs-title">Crosshair</span>
          <span class="xs-hint">C to toggle · Esc to close</span>
        </div>
        <div class="xs-row">
          <label>Color</label>
          <input type="color" data-bind="color" value="${c.color}">
        </div>
        <div class="xs-row">
          <label>Thickness (${c.thickness} px)</label>
          <input type="range" min="1" max="4" step="1" data-bind="thickness" value="${c.thickness}">
        </div>
        <div class="xs-row">
          <label>Length (${c.length} px)</label>
          <input type="range" min="2" max="14" step="1" data-bind="length" value="${c.length}">
        </div>
        <div class="xs-row">
          <label>Static gap (${c.gap} px)</label>
          <input type="range" min="0" max="14" step="1" data-bind="gap" value="${c.gap}">
        </div>
        <div class="xs-row xs-check">
          <label><input type="checkbox" data-bind="dynamic" ${c.dynamic ? "checked" : ""}> Dynamic spread</label>
        </div>
        <div class="xs-row xs-check">
          <label><input type="checkbox" data-bind="outline" ${c.outline ? "checked" : ""}> Outline</label>
        </div>
        <div class="xs-row">
          <label>Outline color</label>
          <input type="color" data-bind="outlineColor" value="${c.outlineColor}">
        </div>
        <div class="xs-row">
          <label>Outline thickness (${c.outlineThickness} px)</label>
          <input type="range" min="0" max="3" step="1" data-bind="outlineThickness" value="${c.outlineThickness}">
        </div>
        <div class="xs-row">
          <label>Style</label>
          <div class="xs-styles">${styleButtons}</div>
        </div>
        <div class="xs-row xs-actions">
          <button class="xs-reset">Reset to default</button>
        </div>
      </div>`;
  }

  private bindControls(): void {
    const $ = (sel: string) => this.root.querySelector(sel) as HTMLElement | null;

    const update = <K extends keyof CrosshairConfig>(key: K, value: CrosshairConfig[K]) => {
      this.cfg = { ...this.cfg, [key]: value };
      notifyCrosshairChange(this.cfg);
      // Re-render labels in-place so the (px) values update next to sliders.
      this.refreshLabels();
    };

    for (const input of Array.from(this.root.querySelectorAll<HTMLInputElement>("input[data-bind]"))) {
      const key = input.dataset.bind as keyof CrosshairConfig;
      const handler = () => {
        if (input.type === "checkbox") {
          update(key, input.checked as never);
        } else if (input.type === "color") {
          update(key, input.value as never);
        } else {
          update(key, Number(input.value) as never);
        }
      };
      input.addEventListener("input", handler);
      input.addEventListener("change", handler);
    }

    for (const btn of Array.from(this.root.querySelectorAll<HTMLButtonElement>(".xs-style"))) {
      btn.addEventListener("click", (e) => {
        const s = (e.currentTarget as HTMLButtonElement).dataset.style as CrosshairStyle;
        update("style", s);
        for (const sib of Array.from(this.root.querySelectorAll<HTMLButtonElement>(".xs-style"))) {
          sib.classList.toggle("active", sib.dataset.style === s);
        }
      });
    }

    $(".xs-reset")?.addEventListener("click", () => {
      this.cfg = {
        color: "#00ff88",
        thickness: 1,
        gap: 3,
        length: 6,
        outline: true,
        outlineColor: "#000000",
        outlineThickness: 1,
        style: "cross",
        dynamic: true,
      };
      notifyCrosshairChange(this.cfg);
      this.root.innerHTML = this.renderHtml();
      this.bindControls();
    });
  }

  private refreshLabels(): void {
    const c = this.cfg;
    const set = (sel: string, txt: string) => {
      const el = this.root.querySelector(sel);
      if (el) el.textContent = txt;
    };
    set('input[data-bind="thickness"]', "");
    // Update text in front of each slider — match by data-bind sibling.
    for (const row of Array.from(this.root.querySelectorAll<HTMLDivElement>(".xs-row"))) {
      const input = row.querySelector<HTMLInputElement>("input[data-bind]");
      const label = row.querySelector<HTMLLabelElement>("label");
      if (!input || !label || input.type !== "range") continue;
      const key = input.dataset.bind as keyof CrosshairConfig;
      if (key === "thickness") label.textContent = `Thickness (${c.thickness} px)`;
      else if (key === "length") label.textContent = `Length (${c.length} px)`;
      else if (key === "gap") label.textContent = `Static gap (${c.gap} px)`;
      else if (key === "outlineThickness") label.textContent = `Outline thickness (${c.outlineThickness} px)`;
    }
  }

  private injectStyles(): void {
    if (document.getElementById("crosshair-settings-styles")) return;
    const style = document.createElement("style");
    style.id = "crosshair-settings-styles";
    style.textContent = `
      #crosshair-settings {
        position: fixed;
        top: 80px;
        right: 16px;
        z-index: 30;
        width: 320px;
        font-family: ui-sans-serif, system-ui, sans-serif;
        font-size: 13px;
        color: var(--text, #f5f5f0);
      }
      #crosshair-settings.hidden { display: none; }
      #crosshair-settings .xs-panel {
        padding: 14px 16px 12px;
        border-radius: 10px;
        border: 1px solid rgba(255, 255, 255, 0.10);
        background: rgba(15, 16, 20, 0.94);
        backdrop-filter: blur(8px);
        display: flex;
        flex-direction: column;
        gap: 10px;
      }
      #crosshair-settings .xs-header {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        margin-bottom: 4px;
      }
      #crosshair-settings .xs-title {
        font-size: 14px;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }
      #crosshair-settings .xs-hint {
        font-family: ui-monospace, SFMono-Regular, monospace;
        font-size: 10px;
        color: var(--text-mute, #6b6b72);
      }
      #crosshair-settings .xs-row {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      #crosshair-settings .xs-row label {
        font-size: 12px;
        color: var(--text-dim, #cfcfd0);
      }
      #crosshair-settings input[type="range"] { width: 100%; }
      #crosshair-settings input[type="color"] {
        width: 56px; height: 24px;
        border-radius: 4px;
        border: 1px solid rgba(255, 255, 255, 0.18);
        background: transparent;
      }
      #crosshair-settings .xs-check label { display: flex; align-items: center; gap: 6px; cursor: pointer; }
      #crosshair-settings .xs-styles {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 6px;
      }
      #crosshair-settings .xs-style,
      #crosshair-settings .xs-reset {
        padding: 6px 8px;
        border-radius: 6px;
        border: 1px solid rgba(255, 255, 255, 0.10);
        background: rgba(255, 255, 255, 0.04);
        color: var(--text, #f5f5f0);
        cursor: pointer;
        font: inherit;
        font-size: 11px;
      }
      #crosshair-settings .xs-style.active {
        border-color: rgba(125, 255, 179, 0.5);
        background: rgba(125, 255, 179, 0.08);
        color: #7dffb3;
      }
      #crosshair-settings .xs-actions {
        margin-top: 4px;
      }
      #crosshair-settings .xs-reset {
        width: 100%;
        font-weight: 600;
      }
    `;
    document.head.appendChild(style);
  }
}

function labelForStyle(s: CrosshairStyle): string {
  switch (s) {
    case "cross":     return "Cross";
    case "t-shape":   return "T";
    case "dot":       return "Dot";
    case "cross-dot": return "Cross+Dot";
  }
}
